import React, {Fragment, useState} from 'react';
import iconNames from "services/icons";
import Icon from "components/Icon";
import {CheckIcon, SelectorIcon} from "@heroicons/react/solid";
import IconName from "types/IconName";
import {Listbox, Transition} from "@headlessui/react";
import {concatenate} from "services/string";

// Types ----

type IconSelectorProps = {
  selected: IconName | string
  onChange: (iconName: IconName) => void
}

// Component ----

function IconSelector(props: IconSelectorProps) {
  const [selected, setSelected] = useState<IconName | string>(props.selected);

  function handleChange(iconName: IconName) {
    setSelected(iconName)
    props.onChange(iconName)
  }

  return (
    <Listbox value={selected} onChange={handleChange}>
      {({open}) => (
        <>
          <Listbox.Label className="block text-sm font-medium text-gray-700">Icon *</Listbox.Label>
          <div className="mt-1 relative">

            <Listbox.Button
              className="sm:w-full relative bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand sm:text-sm">
                <span className="flex items-center">
                  <span className="ml-3 block">
                    <Icon name={selected} className="h-5 w-5"/>
                  </span>
                </span>
              <span className="ml-3 absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <SelectorIcon className="h-5 w-5 text-gray-400" aria-hidden="true"/>
              </span>
            </Listbox.Button>

            <Transition
              show={open}
              appear={true}
              as={Fragment}
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <Listbox.Options
                className="absolute z-10 mt-1 w-28 bg-white shadow-lg max-h-56 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                {iconNames.map(name => (
                  <Listbox.Option
                    key={name}
                    value={name}
                    className={({active}) => concatenate(
                      active ? 'text-white bg-brand' : 'text-gray-900',
                      'cursor-default select-none relative py-2 pl-3 pr-9'
                    )}>

                    {/* TODO: Investigate performance issues relating to hover */}
                    {/* See: https://github.com/tailwindlabs/headlessui/issues/474 */}
                    {({selected, active}) => (
                      <>
                        <div className="flex items-center">
                          <span
                            className={concatenate(selected ? 'font-semibold' : 'font-normal', 'ml-3 block')}>
                              <span className="sr-only">{name}</span>
                              <Icon name={name} className="h-5 w-5"/>
                          </span>
                        </div>

                        {selected && (
                          <span className={concatenate(
                            active ? 'text-white' : 'text-brand',
                            'absolute inset-y-0 right-0 flex items-center pr-4'
                          )}>
                              <CheckIcon className="h-5 w-5" aria-hidden="true"/>
                          </span>
                        )}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
}

IconSelector.defaultProps = {
  selected: IconName.INFORMATION_CIRCLE
}

export default IconSelector;